/*
 * RBC Fit Finder — native measurement walkthrough (replaces the legacy Vue fitfinder app).
 * Faithful, lightly-modernised rebuild: intro + shoe size → per-measurement video steps
 * (Left/Right inputs) → results. Writes the captured values to the product form as
 * line-item properties using the EXACT legacy property names (typos preserved so existing
 * order/admin integrations keep working), and gates add-to-cart for made-to-measure until
 * the measurements are complete.
 *
 * Stage 1 = Made-To-Measure capture + gating. Off-the-shelf size recommendation is layered
 * on in stage 2 (same engine, the `results` step gains a recommended size).
 */
(function () {
  var cfgEl = document.getElementById('rbc-fitfinder-config');
  if (!cfgEl) return;
  var CFG;
  try { CFG = JSON.parse(cfgEl.textContent); } catch (e) { return; }
  if (!CFG) return;
  if (CFG.productType === 'dual') { if (!CFG.otsSteps || !CFG.otsSteps.length) return; }
  else if (!CFG.steps || !CFG.steps.length) return;

  var STORE_KEY = 'rbcFitFinder:' + CFG.productId;
  var PROFILE_KEY = 'rbcFitFinder:profile';
  var state = loadState();        // { values: {prop: value}, shoeSize, complete, otsComplete, mtmComplete }
  var current = 0;                // current step index
  var overlay, stage, prevBtn, nextBtn, progressEl, errEl;

  /* 'dual' products (Fit variant axis) run both flows from one config; `mode` says
     which one the overlay is currently walking. Legacy products have a single mode. */
  var isDual = CFG.productType === 'dual';
  var mode = isDual ? 'ots' : CFG.productType;
  // Rider's overall height: asked once on the MTM intro so the fitting team can
  // sanity-check the leg measurements (43cm below-knee reads differently at 155cm
  // vs 185cm). Attached as an order property alongside the L/R measurements.
  var RIDER_HEIGHT_PROP = 'Rider Height (cm)';
  function isMtmFlow() { return isDual ? mode === 'mtm' : CFG.productType === 'mtm'; }
  function stepsHaveLr(arr) { for (var i = 0; i < (arr || []).length; i++) { if (arr[i].type === 'lr') return true; } return false; }
  function steps() { return isDual ? (mode === 'mtm' ? CFG.mtmSteps : CFG.otsSteps) : CFG.steps; }
  function flowComplete(m) {
    if (!isDual) return !!state.complete;
    return m === 'mtm' ? !!state.mtmComplete : !!state.otsComplete;
  }
  function setFlowComplete(m) {
    if (!isDual) { state.complete = true; return; }
    if (m === 'mtm') state.mtmComplete = true; else state.otsComplete = true;
  }
  function activeTitle() { return (isDual && mode === 'mtm') ? (CFG.mtmTitle || CFG.title) : CFG.title; }
  function activeIntro() { return (isDual && mode === 'mtm') ? (CFG.mtmIntro || CFG.intro) : CFG.intro; }

  function loadState() {
    try {
      var raw = localStorage.getItem(STORE_KEY);
      if (raw) {
        var own = JSON.parse(raw);
        if (own && own.values) {
          // Latest measurements win across products too: a shared profile saved
          // MORE RECENTLY (the rider re-measured on another boot) supersedes this
          // product's stale capture. Unstamped states (pre-fix) keep current behaviour.
          var fresher = seedFromProfile();
          if (fresher && fresher.savedAt && fresher.savedAt > (own.savedAt || 0)) {
            // Persist the adoption: other localStorage readers of this product's
            // store (the size stack's MTM summary / fit grid) must see the
            // resolved state too, not the stale capture it superseded.
            localStorage.setItem(STORE_KEY, JSON.stringify(fresher));
            return fresher;
          }
          deriveOtsFromMtm(own.values); // same-product MTM → standard flow too
          // Parity with the seeded path: derived values that satisfy the
          // standard flow mark it complete so the panel prefils here too.
          if (CFG.productType === 'dual' && !own.otsComplete) own.otsComplete = flowSatisfied(CFG.otsSteps, own);
          else if (CFG.productType === 'ots' && !own.complete) own.complete = flowSatisfied(CFG.steps, own);
          // A standard entry that contradicts the stored L/R capture voids the
          // MTM completion — the walkthrough must be re-taken, not reused.
          if (own.mtmComplete && mtmDisagrees(own.values)) own.mtmComplete = false;
          if (CFG.productType === 'mtm' && own.complete && mtmDisagrees(own.values)) own.complete = false;
          // Requirements can grow (e.g. rider height): a capture completed under the
          // old rules must revalidate, or writeProperties() submits without the new prop.
          if (own.mtmComplete && !flowSatisfied(CFG.mtmSteps, own)) own.mtmComplete = false;
          if (CFG.productType === 'mtm' && own.complete && !flowSatisfied(CFG.steps, own)) own.complete = false;
        }
        return own;
      }
      // First visit to this product: measurements are the RIDER's data, not the
      // boot's — seed from the last-saved profile so the flow never repeats.
      var seeded = seedFromProfile();
      if (seeded) return seeded;
    } catch (e) {}
    return { values: {}, shoeSize: '', complete: false };
  }
  // Every step of `stepsArr` answerable from `candidate`? Mirrors validateStep()
  // (which reads the live `state`, not a candidate — hence the twin).
  function flowSatisfied(stepsArr, candidate) {
    if (!stepsArr || !stepsArr.length) return false;
    for (var i = 0; i < stepsArr.length; i++) {
      var s = stepsArr[i];
      if ((s.type === 'intro' || s.type === 'shoe') && CFG.requireShoe && !candidate.shoeSize) return false;
      if (s.type === 'intro' && stepsHaveLr(stepsArr) && !num(candidate.values[RIDER_HEIGHT_PROP])) return false;
      if (s.type === 'lr' && (!num(candidate.values[s.left.prop]) || !num(candidate.values[s.right.prop]))) return false;
      if (s.type === 'single' && !num(candidate.values[s.input.key])) return false;
    }
    return true;
  }
  // Whitelist seed: ONLY rider-intrinsic data (measurement values + shoe size)
  // crosses products. Completion flags are recomputed against THIS product's
  // flow so a foreign flow's completion can never unlock an unmeasured one, and
  // chosen/chosenManual (a per-boot size choice + its off-chart-guard bypass)
  // never ride along — applyRecommendation() re-derives from scratch.
  function seedFromProfile() {
    var raw = localStorage.getItem(PROFILE_KEY);
    if (!raw) return null;
    var p = JSON.parse(raw);
    if (!p || !p.values) return null;
    var seeded = { values: p.values, shoeSize: p.shoeSize || '', complete: false, savedAt: p.savedAt };
    if (p.heightUnit) seeded.heightUnit = p.heightUnit;   // rider-intrinsic, like the values
    // Foot-size vocabularies differ by brand, but labels usually EMBED the
    // other vocabulary — De Niro's "EU 38 (UK 5)" carries the "5" Parlanti
    // speaks. Adopt the option whose numbers match a numeric token of the
    // saved label, but ONLY when exactly one option matches; ambiguous or no
    // match asks again (a poisoned foot silently breaks the chart lookup).
    if (seeded.shoeSize && CFG.footSizes && CFG.footSizes.length &&
        CFG.footSizes.indexOf(seeded.shoeSize) === -1) {
      var tokens = String(seeded.shoeSize).match(/\d+(\.\d+)?/g) || [];
      var hits = [];
      for (var i = 0; i < CFG.footSizes.length; i++) {
        var optNums = String(CFG.footSizes[i]).match(/\d+(\.\d+)?/g) || [];
        for (var t = 0; t < tokens.length; t++) {
          if (optNums.indexOf(tokens[t]) !== -1) { hits.push(CFG.footSizes[i]); break; }
        }
      }
      seeded.shoeSize = hits.length === 1 ? hits[0] : '';
    }
    // MTM completion is only ever GRANTED by a confirmed walkthrough — carry the
    // source's confirmation across (mtmConfirmed: the L/R values were confirmed
    // and not edited since). Value-presence alone must never re-promote a capture
    // the rider voided mid-edit on another product (Codex round 3). Pre-flag
    // profiles: only a dual-sourced mtmComplete counts — a bare legacy `complete`
    // can't say whether it came from an MTM walkthrough or an OTS size flow
    // (Codex round 4), so those riders re-confirm once, prefilled.
    var srcConfirmed = p.mtmConfirmed !== undefined ? !!p.mtmConfirmed : !!p.mtmComplete;
    seeded.mtmConfirmed = srcConfirmed;
    deriveOtsFromMtm(seeded.values);
    if (CFG.productType === 'dual') {
      seeded.mtmComplete = srcConfirmed && flowSatisfied(CFG.mtmSteps, seeded) && !mtmDisagrees(seeded.values);
      seeded.otsComplete = flowSatisfied(CFG.otsSteps, seeded);
    } else if (CFG.productType === 'mtm') {
      seeded.complete = srcConfirmed && flowSatisfied(CFG.steps, seeded) && !mtmDisagrees(seeded.values);
    } else {
      seeded.complete = flowSatisfied(CFG.steps, seeded);
    }
    return seeded;
  }
  // The larger of the L/R measurements for a dimension. Map via this product's
  // MTM step config when it has one (dual products); legacy single-mode products
  // don't, so fall back to the canonical property names (the fixed
  // order-property vocabulary, B3-verified).
  function largerOf(values, frag, fallbackLeft, fallbackRight) {
    var best = 0;
    ((CFG.mtmSteps || CFG.steps) || []).forEach(function (s) {
      if (s.type !== 'lr' || s.left.prop.indexOf(frag) === -1) return;
      best = Math.max(num(values[s.left.prop]), num(values[s.right.prop]));
    });
    if (!best) best = Math.max(num(values[fallbackLeft]), num(values[fallbackRight]));
    return best;
  }
  // A rider who completed the MTM walkthrough never re-enters the standard flow:
  // derive its two inputs from the L/R pairs. Fit the larger leg — the boot must
  // clear the bigger calf, and height leans tall-when-new by the fitting rules.
  // `force` overwrites existing standard values — used at MTM confirm, where the
  // fresh capture must supersede any earlier standard-flow entry (latest wins;
  // without it a stale standard entry outlived a full re-measure — Simon 2026-07-04).
  function deriveOtsFromMtm(values, force) {
    if (force || !num(values.legHeight)) { var h = largerOf(values, 'Leg Height', 'Left Leg Height', 'Right Leg Height'); if (h) values.legHeight = String(h); }
    if (force || !num(values.calfSize)) { var c = largerOf(values, 'Calf Size', 'Left Calf Size', 'Right Calf Size'); if (c) values.calfSize = String(c); }
  }
  // Reverse direction of latest-wins: the standard keys are DERIVED from the L/R
  // pairs, so a rider who later types a DIFFERENT height/calf in the standard flow
  // has contradicted the saved MTM capture — it no longer describes them and must
  // be re-taken (per-leg data can't be fabricated from one number), never silently
  // reused on an order. True when both sides exist and differ beyond float noise.
  function mtmDisagrees(values) {
    function differs(frag, fallbackLeft, fallbackRight, stdKey) {
      var std = parseFloat(values[stdKey]);
      if (isNaN(std)) return false;
      var best = largerOf(values, frag, fallbackLeft, fallbackRight);
      if (!best) return false;
      return Math.abs(best - std) > 0.05;
    }
    return differs('Leg Height', 'Left Leg Height', 'Right Leg Height', 'legHeight') ||
           differs('Calf Size', 'Left Calf Size', 'Right Calf Size', 'calfSize');
  }
  function saveState() {
    try {
      state.savedAt = Date.now();   // freshness stamp: lets a newer profile beat a stale per-product store
      var json = JSON.stringify(state);
      localStorage.setItem(STORE_KEY, json);
      localStorage.setItem(PROFILE_KEY, json); // rider-level profile, shared across products
    } catch (e) {}
  }

  /* ---------- DOM build ---------- */
  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }
  /* For any string that originates in section settings / config: textContent, never
     innerHTML — a merchant-editable title must not parse as markup. el() with its
     html arg stays for OUR static fragments (arrows, dots) and the icon settings,
     which are SVG by design. */
  function elText(tag, cls, text) {
    var n = el(tag, cls);
    n.textContent = text == null ? '' : text;
    return n;
  }

  function buildOverlay() {
    overlay = el('div', 'ff-overlay');
    overlay.setAttribute('hidden', '');
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', CFG.title || 'Fit guide');

    var scrim = el('div', 'ff-overlay__scrim');
    scrim.addEventListener('click', close);

    var panel = el('div', 'ff-overlay__panel');
    var closeBtn = el('button', 'ff-close', '×');
    closeBtn.type = 'button';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.addEventListener('click', close);

    stage = el('div', 'ff-stage');
    errEl = el('div', 'ff-error');
    errEl.setAttribute('hidden', '');

    var nav = el('div', 'ff-nav');
    prevBtn = el('button', 'ff-btn ff-btn--ghost ff-prev', 'Previous Step');
    prevBtn.type = 'button';
    prevBtn.addEventListener('click', function () { go(current - 1); });
    progressEl = el('div', 'ff-progress');
    nextBtn = el('button', 'ff-btn ff-next', 'Next Step <span aria-hidden="true">→</span>');
    nextBtn.type = 'button';
    nextBtn.addEventListener('click', onNext);
    nav.appendChild(prevBtn);
    nav.appendChild(progressEl);
    nav.appendChild(nextBtn);

    panel.appendChild(closeBtn);
    panel.appendChild(stage);
    panel.appendChild(errEl);
    panel.appendChild(nav);
    overlay.appendChild(scrim);
    overlay.appendChild(panel);
    document.body.appendChild(overlay);
  }

  /* ---------- Step rendering ---------- */
  function renderStep(step) {
    stage.innerHTML = '';
    hideError();
    var wrap = el('div', 'ff-step ff-step--' + step.type);

    if (step.type === 'intro') {
      wrap.appendChild(elText('h2', 'ff-heading', activeTitle()));
      if (activeIntro()) wrap.appendChild(elText('p', 'ff-blurb', activeIntro()));
      // Voided-by-disagreement re-take: say WHY the walkthrough re-opened, in
      // plain words — a non-technical rider must never wonder what happened.
      if ((isDual ? mode === 'mtm' : CFG.productType === 'mtm') &&
          !flowComplete('mtm') && flowSatisfied(steps(), state) && mtmDisagrees(state.values)) {
        wrap.appendChild(elText('p', 'ff-note',
          'Your height or calf measurement has changed since you last completed this walkthrough. ' +
          'Please re-check each step — your previous figures are filled in, so it only takes a moment.'));
      }
      if (CFG.needs && CFG.needs.length) {
        var needs = el('div', 'ff-needs');
        needs.appendChild(elText('p', 'ff-needs__lead', 'For this guide you will need:'));
        var row = el('div', 'ff-needs__row');
        CFG.needs.forEach(function (n) {
          var item = el('div', 'ff-need');
          item.appendChild(el('div', 'ff-need__icon', n.icon || ''));   // SVG by design
          item.appendChild(elText('div', 'ff-need__label', n.label || ''));
          row.appendChild(item);
        });
        needs.appendChild(row);
        wrap.appendChild(needs);
      }
      // shoe size select sits on the intro screen (matches live)
      wrap.appendChild(buildShoe(step));
      if (isMtmFlow()) {
        var hRow = el('div', 'ff-inputs ff-inputs--rider');
        hRow.appendChild(buildRiderHeight());
        wrap.appendChild(hRow);
        wrap.appendChild(elText('p', 'ff-step-hint', 'Your overall height helps our fitting team sanity-check your leg measurements before anything is confirmed.'));
      }
    } else if (step.type === 'shoe') {
      wrap.appendChild(elText('h3', 'ff-step-title', step.title || 'Shoe Size'));
      wrap.appendChild(buildShoe(step));
    } else if (step.type === 'lr') {
      if (step.video) wrap.appendChild(buildVideo(step.video, step.poster));
      wrap.appendChild(elText('h3', 'ff-step-title', step.title));
      if (step.hint) wrap.appendChild(elText('p', 'ff-step-hint', step.hint));
      var inputs = el('div', 'ff-inputs');
      inputs.appendChild(buildMeasure(step.left, 'Left'));
      inputs.appendChild(buildMeasure(step.right, 'Right'));
      wrap.appendChild(inputs);
    } else if (step.type === 'single') {
      if (step.video) wrap.appendChild(buildVideo(step.video, step.poster));
      wrap.appendChild(elText('h3', 'ff-step-title', step.title));
      if (step.hint) wrap.appendChild(elText('p', 'ff-step-hint', step.hint));
      var sinputs = el('div', 'ff-inputs');
      sinputs.appendChild(buildSingleInput(step.input));
      wrap.appendChild(sinputs);
    } else if (step.type === 'pref') {
      wrap.appendChild(elText('h3', 'ff-step-title', step.title));
      if (step.hint) wrap.appendChild(elText('p', 'ff-step-hint', step.hint));
      wrap.appendChild(buildGauge(step));
    } else if (step.type === 'results') {
      if (isDual ? mode === 'ots' : CFG.productType === 'ots') {
        wrap.appendChild(el('h2', 'ff-heading', 'Your Recommended Size'));
        wrap.appendChild(el('p', 'ff-blurb', 'Based on your measurements, we recommend:'));
        wrap.appendChild(buildRecommendation());
      } else {
        wrap.appendChild(el('h2', 'ff-heading', 'Your Measurements'));
        wrap.appendChild(el('p', 'ff-blurb', 'Check everything looks right, then confirm to attach these to your order.'));
        wrap.appendChild(buildSummary());
      }
    }
    stage.appendChild(wrap);
  }

  function buildShoe(step) {
    var field = el('div', 'ff-field');
    var id = 'ff-shoe';
    field.appendChild(labelFor(id, step.optionLabel || 'Foot Size'));
    var sel = el('select', 'ff-select');
    sel.id = id;
    sel.appendChild(new Option('Please select one', ''));
    (CFG.footSizes || []).forEach(function (v) {
      var o = new Option(v, v);
      if (v === state.shoeSize) o.selected = true;
      sel.appendChild(o);
    });
    sel.addEventListener('change', function () {
      state.shoeSize = sel.value;
      selectFootVariant(sel.value);
      state.chosenManual = false; state.chosen = null;  // inputs changed → recompute recommendation
      saveState();
      hideError();
    });
    field.appendChild(sel);
    return field;
  }

  function buildMeasure(spec, side) {
    var field = el('div', 'ff-field ff-field--measure');
    var id = 'ff-' + spec.id;
    field.appendChild(labelFor(id, side + ' (cm)'));
    var inWrap = el('div', 'ff-measure');
    var input = el('input', 'ff-input');
    input.type = 'number';
    input.id = id;
    input.min = '0';
    input.step = '0.1';
    input.inputMode = 'decimal';
    input.placeholder = '0';
    input.setAttribute('data-prop', spec.prop);
    if (state.values[spec.prop]) input.value = state.values[spec.prop];
    input.addEventListener('input', function () {
      state.values[spec.prop] = input.value;
      state.chosenManual = false; state.chosen = null;  // measurement changed → recompute recommendation
      // An edit to a confirmed capture voids it until re-confirmed — the ATC gate
      // must never submit half-edited or unconfirmed L/R order properties.
      // mtmConfirmed rides in the shared profile so the void holds on EVERY
      // product, not just this one.
      state.mtmConfirmed = false;
      if (isDual) state.mtmComplete = false;
      else if (CFG.productType === 'mtm') state.complete = false;
      saveState();
      hideError();
    });
    inWrap.appendChild(input);
    inWrap.appendChild(el('span', 'ff-unit', 'cm'));
    field.appendChild(inWrap);
    return field;
  }

  function buildSingleInput(spec) {
    var field = el('div', 'ff-field ff-field--measure');
    var id = 'ff-' + spec.key;
    field.appendChild(labelFor(id, 'Measurement (cm)'));
    var inWrap = el('div', 'ff-measure');
    var input = el('input', 'ff-input');
    input.type = 'number';
    input.id = id;
    input.min = '0';
    input.step = '0.1';
    input.inputMode = 'decimal';
    input.placeholder = '0';
    input.setAttribute('data-key', spec.key);
    if (state.values[spec.key]) input.value = state.values[spec.key];
    input.addEventListener('input', function () {
      state.values[spec.key] = input.value;
      state.chosenManual = false; state.chosen = null;  // measurement changed → recompute recommendation
      // A standard entry that now contradicts the confirmed L/R capture voids it
      // IMMEDIATELY, not just at confirm — a mid-flow abandon must not leave the
      // ATC gate trusting stale measurement properties.
      if (isDual && state.mtmComplete && mtmDisagrees(state.values)) state.mtmComplete = false;
      saveState();
      hideError();
    });
    inWrap.appendChild(input);
    inWrap.appendChild(el('span', 'ff-unit', 'cm'));
    field.appendChild(inWrap);
    return field;
  }

  /* ---------- Rider height (cm / ft+in unit switch) ---------- */
  /* Many customers think in feet & inches (Simon 2026-07-08). Canonical storage
     stays CENTIMETRES in state.values[RIDER_HEIGHT_PROP] — the order property
     name and the fitting team's cm expectations don't change; ft/in is an
     entry/display skin over it. The chosen unit persists (state.heightUnit)
     and rides the shared profile like other rider-intrinsic data. */
  function cmToFtIn(cm) {
    var ti = cm / 2.54;
    var ft = Math.floor(ti / 12);
    var inch = Math.round((ti - ft * 12) * 2) / 2;   // nearest half-inch
    if (inch >= 12) { ft += 1; inch -= 12; }
    return { ft: ft, inch: inch };
  }
  // Same voiding rules as any measurement edit (buildMeasure): a changed height
  // invalidates the confirmed capture until re-confirmed, on every product.
  function riderHeightEdited() {
    state.chosenManual = false; state.chosen = null;
    state.mtmConfirmed = false;
    if (isDual) state.mtmComplete = false;
    else if (CFG.productType === 'mtm') state.complete = false;
    saveState();
    hideError();
  }
  function buildRiderHeight() {
    var field = el('div', 'ff-field ff-field--measure ff-field--rider');
    var head = el('div', 'ff-rider-head');
    head.appendChild(labelFor('ff-rider-height', 'Your height'));
    var toggle = el('div', 'ff-unit-toggle');
    toggle.setAttribute('role', 'group');
    toggle.setAttribute('aria-label', 'Height unit');
    var bCm = el('button', 'ff-unit-btn', 'cm');
    bCm.type = 'button';
    var bFt = el('button', 'ff-unit-btn', 'ft / in');
    bFt.type = 'button';
    toggle.appendChild(bCm);
    toggle.appendChild(bFt);
    head.appendChild(toggle);
    field.appendChild(head);

    // cm entry
    var cmWrap = el('div', 'ff-measure');
    var cmInput = el('input', 'ff-input');
    cmInput.type = 'number'; cmInput.id = 'ff-rider-height';
    cmInput.min = '0'; cmInput.step = '0.1'; cmInput.inputMode = 'decimal'; cmInput.placeholder = '170';
    cmWrap.appendChild(cmInput);
    cmWrap.appendChild(el('span', 'ff-unit', 'cm'));

    // ft + in entry
    var ftRow = el('div', 'ff-ftin');
    function ftinField(ph, aria, max, stepAttr) {
      var w = el('div', 'ff-measure');
      var i = el('input', 'ff-input');
      i.type = 'number'; i.min = '0'; i.max = max; i.step = stepAttr;
      i.inputMode = 'decimal'; i.placeholder = ph;
      i.setAttribute('aria-label', aria);
      w.appendChild(i);
      w.appendChild(el('span', 'ff-unit', aria === 'Feet' ? 'ft' : 'in'));
      return { wrap: w, input: i };
    }
    var ft = ftinField('5', 'Feet', '8', '1');
    var inch = ftinField('8', 'Inches', '11.5', '0.5');
    ftRow.appendChild(ft.wrap);
    ftRow.appendChild(inch.wrap);

    field.appendChild(cmWrap);
    field.appendChild(ftRow);

    function prefill() {
      var cm = num(state.values[RIDER_HEIGHT_PROP]);
      if (cm) {
        cmInput.value = state.values[RIDER_HEIGHT_PROP];
        var fi = cmToFtIn(cm);
        ft.input.value = fi.ft;
        inch.input.value = fi.inch;
      } else {
        // Cleared height must clear BOTH units — otherwise switching units
        // resurfaces the stale old value in the other inputs (Codex P2).
        cmInput.value = '';
        ft.input.value = '';
        inch.input.value = '';
      }
    }
    function setUnit(u, persist) {
      var ftin = u === 'ftin';
      cmWrap.style.display = ftin ? 'none' : '';
      ftRow.style.display = ftin ? '' : 'none';
      bCm.setAttribute('aria-pressed', String(!ftin));
      bFt.setAttribute('aria-pressed', String(ftin));
      if (persist && state.heightUnit !== u) {
        state.heightUnit = u;
        prefill();       // show the saved value in the newly chosen unit
        saveState();     // unit switch alone never voids the capture
      }
    }
    bCm.addEventListener('click', function () { setUnit('cm', true); });
    bFt.addEventListener('click', function () { setUnit('ftin', true); });

    cmInput.addEventListener('input', function () {
      state.values[RIDER_HEIGHT_PROP] = cmInput.value;
      var cm = num(cmInput.value);
      if (cm) { var fi = cmToFtIn(cm); ft.input.value = fi.ft; inch.input.value = fi.inch; }
      else { ft.input.value = ''; inch.input.value = ''; }
      riderHeightEdited();
    });
    function onFtIn() {
      var f = parseFloat(ft.input.value), i = parseFloat(inch.input.value);
      if (isNaN(f) && isNaN(i)) { state.values[RIDER_HEIGHT_PROP] = ''; cmInput.value = ''; riderHeightEdited(); return; }
      var cm = Math.round(((isNaN(f) ? 0 : f) * 30.48 + (isNaN(i) ? 0 : i) * 2.54) * 10) / 10;
      state.values[RIDER_HEIGHT_PROP] = String(cm);
      cmInput.value = cm;
      riderHeightEdited();
    }
    ft.input.addEventListener('input', onFtIn);
    inch.input.addEventListener('input', onFtIn);

    prefill();
    setUnit(state.heightUnit === 'ftin' ? 'ftin' : 'cm', false);
    return field;
  }

  /* ---------- Preference gauge (the measuring-tape control) ---------- */
  function buildGauge(step) {
    var key = step.key;
    var idx = parseInt(state.values[key], 10);
    if (isNaN(idx) || idx < 0 || idx > 4) idx = 2;
    var g = el('div', 'ff-gauge');
    g.setAttribute('role', 'radiogroup');
    g.setAttribute('aria-label', step.title);
    var track = el('div', 'ff-gauge__track');
    var ticks = el('div', 'ff-gauge__ticks');
    ticks.setAttribute('aria-hidden', 'true');
    track.appendChild(ticks);
    var needle = el('div', 'ff-gauge__needle');
    needle.setAttribute('aria-hidden', 'true');
    var valName = el('em', 'ff-gauge__name');
    var valCap = el('div', 'ff-gauge__cap');
    function setIdx(i, fromUser) {
      idx = i;
      // Only a USER change invalidates the size choice. Since reopen-at-step-1
      // (Decision 5) re-renders this gauge on every pass through a completed
      // flow, an unconditional clear here would discard a manually-adjusted
      // size just for stepping past it unchanged (Codex P1, 2026-07-08).
      if (fromUser) {
        state.values[key] = i;
        state.chosenManual = false; state.chosen = null;   // preference changed → recompute
        saveState();
      }
      needle.style.left = (10 + i * 20) + '%';
      valName.textContent = step.stops[i];
      var cap = (step.captions && step.captions[i]) || '';
      valCap.textContent = cap;
      valCap.style.visibility = cap ? 'visible' : 'hidden';
      var ins = g.querySelectorAll('input');
      for (var k = 0; k < ins.length; k++) ins[k].checked = (+ins[k].value === i);
    }
    step.stops.forEach(function (label, i) {
      var zone = el('label', 'ff-gauge__zone');
      var input = document.createElement('input');
      input.type = 'radio';
      input.name = 'ffg-' + key;
      input.value = String(i);
      input.className = 'ff-gauge__input';
      input.setAttribute('aria-label', label);
      input.addEventListener('change', function () { setIdx(i, true); });
      zone.appendChild(input);
      zone.appendChild(el('span', 'ff-gauge__stop'));
      track.appendChild(zone);
    });
    track.appendChild(needle);
    g.appendChild(track);
    var ends = el('div', 'ff-gauge__ends');
    ends.appendChild(elText('span', null, step.stops[0]));
    ends.appendChild(elText('span', null, step.stops[step.stops.length - 1]));
    g.appendChild(ends);
    var valueEl = el('div', 'ff-gauge__value');
    valueEl.appendChild(valName);
    valueEl.appendChild(valCap);
    g.appendChild(valueEl);
    setIdx(idx);
    return g;
  }

  function buildRecommendation() {
    var rec = otsRec();
    var box = el('div', 'ff-rec');
    if (!rec || (!rec.leg && !rec.calf)) {
      box.appendChild(el('p', 'ff-rec__lead', 'We could not match a standard size from those measurements.'));
      box.appendChild(verdictBanner('outside', null));
      return box;
    }
    // Store the full option LABEL ("EU 38 (UK 5)") — variant radios exact-match on it;
    // a bare "38" could never restore the foot size on a later visit.
    var auto = { foot: state.shoeSize || String(rec.shoe), heightName: rec.leg ? rec.leg.name : '', calfName: rec.calf ? rec.calf.name : '' };
    if (!state.chosenManual || !state.chosen) {
      state.chosen = { foot: auto.foot, heightName: auto.heightName, calfName: auto.calfName, route: state.chosen && state.chosen.route };
    }
    var body = el('div', 'ff-rec__body');
    function rerender() {
      body.innerHTML = '';
      var ch = state.chosen;
      var code = heightCodeForName(rec.legP, ch.heightName);
      var tall = isTallName(rec.legP, ch.heightName);
      var comp = el('div', 'ff-size');
      // textContent (never innerHTML): foot/height/calf come from option values + chart names + state.
      var footNum = shoeNumber(ch.foot);
      var footDisplay = isNaN(footNum) ? (ch.foot || '\u2014') : String(footNum);
      var codeEl = el('div', 'ff-size__code');
      codeEl.textContent = [footDisplay, code || '\u2014', ch.calfName || '\u2014'].join(' \u2013 ');
      comp.appendChild(codeEl);
      var humanEl = el('div', 'ff-size__human');
      humanEl.textContent = ['Foot ' + footDisplay, ch.heightName ? ch.heightName + ' height' : '', ch.calfName ? ch.calfName + ' calf' : ''].filter(Boolean).join(' \u00b7 ') + (tall ? '  \u00b7  Tall \u2014 +\u00a335' : '');
      comp.appendChild(humanEl);
      body.appendChild(comp);
      body.appendChild(compareBlock(rec));
      body.appendChild(sizeEditor(rec, rerender));
      body.appendChild(verdictBanner(rec.verdict, rec));
    }
    rerender();
    box.appendChild(body);
    return box;
  }

  function compareBlock(rec) {
    var wrap = el('div', 'ff-compare');
    wrap.appendChild(el('p', 'ff-rec__lead', 'How your measurements compare to our standard sizes:'));
    wrap.appendChild(compareRow('Leg height', rec.rawLeg, rec.leg, 'leg'));
    wrap.appendChild(compareRow('Calf width', rec.rawCalf, rec.calf, 'calf'));
    return wrap;
  }
  /* Exact boot measurement for the customer's foot (chart.values, per-foot data —
     De Niro only; other brands return null and rows stay delta-only). */
  function nominalFor(kind, axis) {
    var vals = CFG.chart && CFG.chart.values;
    if (!vals || !axis) return null;
    var shoe = shoeNumber(state.shoeSize);
    if (isNaN(shoe)) return null;
    shoe = CFG.chart.hasHalfSizes ? Math.round(shoe * 2) / 2 : Math.round(shoe);
    var table = kind === 'leg' ? vals.heights : vals.calves;
    var key = kind === 'leg' ? DN_HEIGHT_CODES[axis.idx] : axis.name;
    // Non-De-Niro charts (Parlanti) key their heights by RANGE NAME, not DN codes.
    if (kind === 'leg' && table && !(key in table) && (axis.name in table)) key = axis.name;
    var v = table && table[key] && table[key][String(shoe)];
    return (v || v === 0) ? v : null;
  }
  function compareRow(label, raw, axis, kind) {
    var r = el('div', 'ff-compare__row ff-compare__row--' + (axis ? axis.status : 'none'));
    r.appendChild(el('span', 'ff-compare__label', label));
    var v = el('span', 'ff-compare__val');
    v.textContent = (raw > 0 ? raw + ' cm' : '—');
    r.appendChild(v);
    var sz = el('span', 'ff-compare__size');
    if (!axis) sz.textContent = '—';
    else {
      var labels = {
        good: 'good fit', snug: 'snug', baggy: 'roomy',
        loose: 'looser than we advise', short: 'on the short side',
        tall: 'tall — close behind the knee', outside: 'off the chart'
      };
      // Ranges stay internal (Simon 2026-07-03): the size's EXACT measurement for
      // this foot (yours-vs-size, eyeballable) + honest verdict + distance.
      var suffix = labels[axis.status] || 'borderline';
      var nom = nominalFor(kind, axis);
      var hasNom = nom || nom === 0;
      var nomTxt = hasNom ? ' — this size ' + nom + 'cm' : '';
      // Any quoted distance must be the subtraction of the two numbers ON SHOW
      // (yours vs the size's exact value) — never the internal band maths, which
      // sits 0.4–0.5cm off the nominal and reads as wrong arithmetic ("34 vs 31 ·
      // 2.6cm" — Simon 2026-07-06). Bands keep powering the STATUS only; a 'good'
      // verdict carries no distance at all ("good fit by 0.6cm" read as a riddle),
      // and without a nominal on show there is no honest number — verdict word only.
      var dist = hasNom ? Math.round(Math.abs(raw - nom) * 10) / 10 : 0;
      var by = dist ? ' by ' + dist + 'cm' : '';
      if (axis.status === 'good') sz.textContent = axis.name + nomTxt + ' · good fit';
      else if (axis.status === 'outside') sz.textContent = (axis.dir === 'above' ? 'beyond ' : 'below ') + axis.name + nomTxt + ' · off the chart' + by;
      else sz.textContent = axis.name + nomTxt + ' · ' + suffix + by;
    }
    r.appendChild(sz);
    return r;
  }
  function sizeEditor(rec, onchange) {
    var box = el('details', 'ff-edit');
    box.appendChild(el('summary', 'ff-edit__sum', 'Not quite right? Adjust your size'));
    var grid = el('div', 'ff-edit__grid');
    grid.appendChild(editSelect('Foot', CFG.footSizes || [], state.chosen.foot, function (v) { state.chosen.foot = v; }));
    grid.appendChild(editSelect('Height', rec.legP.map(function (x) { return x.name; }), state.chosen.heightName, function (v) { state.chosen.heightName = v; }));
    grid.appendChild(editSelect('Calf', rec.calfP.map(function (x) { return x.name; }), state.chosen.calfName, function (v) { state.chosen.calfName = v; }));
    box.appendChild(grid);
    box.addEventListener('change', function () { state.chosenManual = true; saveState(); onchange(); });
    return box;
  }
  function editSelect(label, values, cur, cb) {
    var f = el('div', 'ff-field');
    var id = 'ff-edit-' + label.toLowerCase();
    f.appendChild(labelFor(id, label));
    var sel = el('select', 'ff-select');
    sel.id = id;
    values.forEach(function (v) { var o = new Option(v, v); if (String(v) === String(cur)) o.selected = true; sel.appendChild(o); });
    sel.addEventListener('change', function () { cb(sel.value); });
    f.appendChild(sel);
    return f;
  }
  function verdictBanner(level, rec) {
    var b = el('div', 'ff-verdict ff-verdict--' + (level === 'loose' ? 'edge' : level));
    var stretch = CFG.fit && CFG.fit.stretch;
    var msg;
    if (level === 'good') msg = 'Great — a standard size fits you well.';
    else if (level === 'loose') msg = 'That’s looser than we’d fit you in store — boots also settle about 1.5cm as they wear in, so expect real room. It’s your call, but made-to-measure would fit exactly.';
    else if (level === 'edge') msg = stretch
      ? 'A close fit — this style’s stretch panel gives, so it should settle in well. Made-to-measure guarantees it exactly.'
      : 'Your measurements sit right at the edge of a standard size. It should fit, but made-to-measure guarantees it exactly.';
    else msg = 'Your measurements fall outside our standard sizes. We’d recommend made-to-measure so your boots fit properly — every pair is made to order either way.';
    b.appendChild(el('p', 'ff-verdict__msg', msg));
    if (level !== 'good') {
      if (!isDual || CFG.mtmCapable) b.appendChild(mtmCta(level === 'outside' ? 'strong' : 'soft'));
      else if (level === 'outside') b.appendChild(el('p', 'ff-verdict__msg', 'This style comes in standard sizes only — get in touch and we’ll talk through the closest fit.'));
    }
    return b;
  }
  function mtmCta(weight) {
    var btn = el('button', 'ff-btn ff-btn--mtm' + (weight === 'soft' ? ' ff-btn--ghost' : ''), 'Switch to made-to-measure');
    btn.type = 'button';
    btn.addEventListener('click', function () {
      state.chosen = state.chosen || {};
      state.chosen.route = 'made-to-measure';
      saveState();
      // The size stack owns the Fit variant: rbc:choose-mtm flips its toggle, which
      // selects the +£160 Made to Measure variant. Then continue straight into the
      // measurement walkthrough without leaving the overlay.
      document.dispatchEvent(new CustomEvent('rbc:choose-mtm', { detail: { productId: CFG.productId } }));
      if (isDual && CFG.mtmCapable) { mode = 'mtm'; go(0); relabelTrigger(); return; }
      selectPropertyOption('Fit', 'Made to measure');
    });
    return btn;
  }

  /* ---------- OTS size recommendation (ported from the legacy chart logic) ---------- */
  function recommend() {
    if (!CFG.chart || !CFG.chart.found) return null;
    var shoe = shoeNumber(state.shoeSize);
    if (isNaN(shoe)) return null;
    shoe = CFG.chart.hasHalfSizes ? Math.round(shoe * 2) / 2 : Math.round(shoe);
    var calf = parseFloat(state.values.calfSize);
    var leg = parseFloat(state.values.legHeight);
    if (!isNaN(calf)) calf += modFor(CFG.mods && CFG.mods.calf, shoe);
    if (!isNaN(leg)) leg += modFor(CFG.mods && CFG.mods.height, shoe);
    return {
      shoe: shoe,
      calfSize: isNaN(calf) ? '' : matchRange(CFG.chart.calfRanges, calf),
      legHeight: isNaN(leg) ? '' : matchRange(CFG.chart.legRanges, leg)
    };
  }
  function modFor(modStr, shoe) {
    if (!modStr) return 0;
    var parts = modStr.split(';');
    for (var i = 0; i < parts.length; i++) {
      var kv = parts[i].split('|');
      if (kv.length === 2 && parseFloat(kv[0]) === shoe) { var m = parseFloat(kv[1]); return isNaN(m) ? 0 : m; }
    }
    return 0;
  }
  function matchRange(ranges, val) {
    if (!ranges) return '';
    for (var i = 0; i < ranges.length; i++) {
      var s = ranges[i];
      var nm = s.match(/\(([^)]*)\)/);
      var name = nm ? nm[1].trim() : '';
      var rp = s.replace(/\([^)]*\)/, '').replace(/cm/ig, '').trim();
      var lo = parseFloat(rp.split('-')[0]);
      var hi = parseFloat(rp.split('-')[1]);
      if (!isNaN(lo) && !isNaN(hi) && val >= lo && val <= hi) return name;
    }
    return '';
  }
  /* ---------- OTS: detailed, honest size match ---------- */
  var DN_HEIGHT_CODES = ['CC', 'C', 'MC', 'MA', 'A'];
  function parseRanges(ranges) {
    return (ranges || []).map(function (s, i) {
      var nm = String(s).match(/\(([^)]*)\)/);
      var name = nm ? nm[1].trim() : String(i + 1);
      var rp = String(s).replace(/\([^)]*\)/, '').replace(/cm/ig, '').trim();
      return { name: name, lo: parseFloat(rp.split('-')[0]), hi: parseFloat(rp.split('-')[1]), idx: i };
    }).filter(function (r) { return !isNaN(r.lo) && !isNaN(r.hi); });
  }
  function locate(parsed, val) {
    if (!parsed.length || isNaN(val)) return null;
    for (var i = 0; i < parsed.length; i++) if (val >= parsed[i].lo && val <= parsed[i].hi) return { range: parsed[i], status: 'in' };
    var first = parsed[0], last = parsed[parsed.length - 1];
    if (val < first.lo) return { range: first, status: 'below' };
    if (val > last.hi) return { range: last, status: 'above' };
    var near = first, best = Infinity;
    parsed.forEach(function (p) { var d = Math.min(Math.abs(val - p.lo), Math.abs(val - p.hi)); if (d < best) { best = d; near = p; } });
    return { range: near, status: 'between' };
  }
  /* Simon's fitting rules (HANDOFF "Fit Finder v2"): how far a measurement may sit
     outside a size's chart range before the fit stops being sellable, by construction.
     For CALF: measurement ABOVE the range = boot smaller than the leg (snug side);
     BELOW = boot roomier than the leg (loose side). For LEG HEIGHT: ABOVE = boot on
     the short side (safer); BELOW = boot tall behind the knee (riskier, worse on
     stiff dressage legs). All values are cm beyond the range bound. */
  function calfTol() {
    var fit = CFG.fit || {};
    if (fit.stretch) return { aboveGood: 1.0, aboveEdge: 1.5, belowGood: 0.5, belowEdge: 1.0, belowMax: 1.5 };
    if (fit.dressage) return { aboveGood: 0, aboveEdge: 0.3, belowGood: 0.5, belowEdge: 1.0, belowMax: 1.5 };
    return { aboveGood: 0, aboveEdge: 0.5, belowGood: 0.5, belowEdge: 1.0, belowMax: 1.5 };
  }
  function legTol() {
    var fit = CFG.fit || {};
    var tall = fit.dressage ? 0.5 : 1.0;
    return { aboveGood: 0, aboveEdge: 1.0, belowGood: 0, belowEdge: tall, belowMax: tall };
  }
  function axisFor(range, val, kind) {
    var t = kind === 'calf' ? calfTol() : legTol();
    var st = 'good', d = 0, dir = 'in';
    if (val > range.hi) {
      d = val - range.hi; dir = 'above';
      st = d <= t.aboveGood ? 'good' : d <= t.aboveEdge ? (kind === 'calf' ? 'snug' : 'short') : 'outside';
    } else if (val < range.lo) {
      d = range.lo - val; dir = 'below';
      st = d <= t.belowGood ? 'good'
        : d <= t.belowEdge ? (kind === 'calf' ? 'baggy' : 'tall')
        : (kind === 'calf' && d <= t.belowMax ? 'loose' : 'outside');
    }
    return { name: range.name, idx: range.idx, lo: range.lo, hi: range.hi, status: st, dir: dir, dist: Math.round(d * 10) / 10 };
  }
  function statusRank(a) {
    return a.status === 'good' ? 0 : a.status === 'outside' ? 99 : a.status === 'loose' ? 2 : 1;
  }
  /* Shop-floor candidate choice: consider the located size and its neighbours, then
     let the customer's stated preference tip the balance between workable options —
     "we want the customer to love the boots, with a nudge towards correctness". */
  function chooseAxis(parsed, val, kind, prefIdx) {
    var loc = locate(parsed, val);
    if (!loc) return null;
    var base = axisFor(loc.range, val, kind);
    var cands = [base];
    if (parsed[loc.range.idx - 1]) cands.push(axisFor(parsed[loc.range.idx - 1], val, kind));
    if (parsed[loc.range.idx + 1]) cands.push(axisFor(parsed[loc.range.idx + 1], val, kind));
    var pref = (prefIdx === undefined || isNaN(prefIdx)) ? 2 : prefIdx;
    var dir = pref < 2 ? -1 : pref > 2 ? 1 : 0;      // -1 = smaller/tighter/shorter code
    var strength = Math.abs(pref - 2);
    var usable = cands.filter(function (a) {
      if (statusRank(a) >= 99) return false;
      if (a.status === 'loose' && pref !== 4) return false;  // truly loose only if they insist
      return true;
    });
    if (!usable.length) return base;
    usable.sort(function (a, b) {
      function score(x) {
        var s = statusRank(x);
        if (dir !== 0 && (x.idx - base.idx) * dir > 0) s -= 0.4 * strength;
        return s;
      }
      return score(a) - score(b) || Math.abs(a.idx - base.idx) - Math.abs(b.idx - base.idx);
    });
    return usable[0];
  }
  /* Foot option values may be bare numbers ("38") or labelled ("EU 38 (UK 5)") —
     the chart works in EU numbers, so pull the first number out. */
  function shoeNumber(v) {
    var m = String(v).match(/\d+(\.\d+)?/);
    return m ? parseFloat(m[0]) : NaN;
  }
  function otsRec() {
    if (!CFG.chart || !CFG.chart.found) return null;
    var shoe = shoeNumber(state.shoeSize); if (isNaN(shoe)) return null;
    shoe = CFG.chart.hasHalfSizes ? Math.round(shoe * 2) / 2 : Math.round(shoe);
    var legP = parseRanges(CFG.chart.legRanges), calfP = parseRanges(CFG.chart.calfRanges);
    var rawLeg = parseFloat(state.values.legHeight), rawCalf = parseFloat(state.values.calfSize);
    var adjLeg = isNaN(rawLeg) ? NaN : rawLeg + modFor(CFG.mods && CFG.mods.height, shoe);
    var adjCalf = isNaN(rawCalf) ? NaN : rawCalf + modFor(CFG.mods && CFG.mods.calf, shoe);
    var leg = isNaN(adjLeg) ? null : chooseAxis(legP, adjLeg, 'leg', parseInt(state.values.prefHeight, 10));
    var calf = isNaN(adjCalf) ? null : chooseAxis(calfP, adjCalf, 'calf', parseInt(state.values.prefCalf, 10));
    var worst = Math.max(leg ? statusRank(leg) : 0, calf ? statusRank(calf) : 0);
    return {
      shoe: shoe, rawLeg: rawLeg, rawCalf: rawCalf, adjLeg: adjLeg, adjCalf: adjCalf,
      legP: legP, calfP: calfP, leg: leg, calf: calf,
      legCode: leg && legP.length === 5 ? DN_HEIGHT_CODES[leg.idx] : (leg ? leg.name : ''),
      verdict: worst >= 99 ? 'outside' : worst === 2 ? 'loose' : worst === 1 ? 'edge' : 'good'
    };
  }
  function heightCodeForName(legP, name) {
    var r = legP.filter(function (x) { return x.name === name; })[0];
    return (r && legP.length === 5) ? DN_HEIGHT_CODES[r.idx] : name;
  }
  function isTallName(legP, name) {
    var r = legP.filter(function (x) { return x.name === name; })[0];
    return !!(r && legP.length === 5 && r.idx >= 3);
  }
  function selectPropertyOption(propName, value) {
    if (!value) return false;
    var sel = document.querySelector('select[name="properties[' + propName + ']"]');
    if (!sel) {
      var all = document.querySelectorAll('select');
      for (var i = 0; i < all.length; i++) {
        if ((all[i].getAttribute('name') || '').indexOf('properties[' + propName + ']') !== -1) { sel = all[i]; break; }
      }
    }
    if (!sel) return false;
    var pick = function (opt) {
      sel.value = opt.value;
      sel.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    };
    // Two passes, EXACT before fuzzy — a single combined pass let the calf
    // recommendation "S" prefix-match the placeholder's "Select calf" text
    // (first option in the list) and beat its own exact "S" option further
    // down. Placeholders (empty value) never match at all.
    var j, ov, ot;
    for (j = 0; j < sel.options.length; j++) {
      ov = String(sel.options[j].value); ot = String(sel.options[j].textContent || '').trim();
      if (!ov) continue;
      if (ov === value || ot === value) return pick(sel.options[j]);
    }
    for (j = 0; j < sel.options.length; j++) {
      ov = String(sel.options[j].value); ot = String(sel.options[j].textContent || '').trim();
      if (!ov) continue;
      // prefix, then substring — substring lets "(MA)" codes match "Tall (MA)".
      if (ov.indexOf(value) === 0 || ot.indexOf(value) === 0 || ov.indexOf(value) !== -1) return pick(sel.options[j]);
    }
    return false;
  }

  function buildVideo(src, poster) {
    var box = el('div', 'ff-video');
    var v = el('video', 'ff-video__el');
    v.src = src;
    v.setAttribute('playsinline', '');
    v.setAttribute('preload', 'metadata');
    v.controls = false;
    if (poster) v.poster = poster;
    var play = el('button', 'ff-video__play', '<span aria-hidden="true">▶</span>');
    play.type = 'button';
    play.setAttribute('aria-label', 'Play instructional video');
    function toggle() {
      if (v.paused) { v.play(); v.controls = true; box.classList.add('is-playing'); }
      else { v.pause(); }
    }
    play.addEventListener('click', toggle);
    v.addEventListener('click', toggle);
    v.addEventListener('ended', function () { box.classList.remove('is-playing'); v.controls = false; });
    box.appendChild(v);
    box.appendChild(play);
    return box;
  }

  function buildSummary() {
    // L/R pairs share a row (measurement | left | right): half the height of a
    // flat list, and asymmetric legs are visible at a glance.
    var tbl = el('div', 'ff-summary');
    if (state.shoeSize) tbl.appendChild(summaryRow('Foot Size', state.shoeSize));
    var lr = steps().filter(function (s) { return s.type === 'lr'; });
    if (lr.length) {
      var head = el('div', 'ff-summary__row ff-summary__head');
      head.appendChild(el('span', 'ff-summary__k'));
      head.appendChild(elText('span', 'ff-summary__v', 'Left'));
      head.appendChild(elText('span', 'ff-summary__v', 'Right'));
      tbl.appendChild(head);
      lr.forEach(function (s) {
        var r = el('div', 'ff-summary__row');
        var ks = el('span', 'ff-summary__k');
        ks.textContent = s.title;       // user/merchant-derived → textContent, never innerHTML
        var lv = el('span', 'ff-summary__v');
        lv.textContent = state.values[s.left.prop] ? state.values[s.left.prop] + ' cm' : '—';
        var rv = el('span', 'ff-summary__v');
        rv.textContent = state.values[s.right.prop] ? state.values[s.right.prop] + ' cm' : '—';
        r.appendChild(ks); r.appendChild(lv); r.appendChild(rv);
        tbl.appendChild(r);
      });
    }
    return tbl;
  }
  function summaryRow(k, v) {
    var r = el('div', 'ff-summary__row ff-summary__row--single');
    var ks = el('span', 'ff-summary__k');
    ks.textContent = k;                 // user/merchant-derived → textContent, never innerHTML
    var vs = el('span', 'ff-summary__v');
    vs.textContent = v;                 // captured measurement values are never parsed as HTML
    r.appendChild(ks);
    r.appendChild(vs);
    return r;
  }

  function labelFor(id, text) {
    var l = elText('label', 'ff-label', text);
    l.setAttribute('for', id);
    return l;
  }

  /* ---------- Navigation + validation ---------- */
  function onNext() {
    var step = steps()[current];
    var bad = validateStep(step);
    if (bad) { showError(bad); return; }
    if (current >= steps().length - 1) {
      // Standard-sizes-only style with off-chart measurements: "Confirm size" must not
      // pretend success — hold on the results screen unless they deliberately picked
      // a size via the editor.
      var otsMode = isDual ? mode === 'ots' : CFG.productType === 'ots';
      if (otsMode && isDual && !CFG.mtmCapable && !state.chosenManual) {
        var rec = otsRec();
        if (rec && rec.verdict === 'outside') {
          showError('These measurements sit outside this style’s standard sizes. Use “Adjust your size” to pick the closest fit, or get in touch and we’ll help.');
          return;
        }
      }
      finish();
      return;
    }
    go(current + 1);
  }

  function validateStep(step) {
    if (step.type === 'intro' || step.type === 'shoe') {
      if (CFG.requireShoe && !state.shoeSize) return 'Please choose your foot size.';
      if (step.type === 'intro' && isMtmFlow()) {
        var rh = num(state.values[RIDER_HEIGHT_PROP]);
        if (!rh) return 'Please enter your height — it helps us check your measurements.';
        if (rh < 100 || rh > 230) return state.heightUnit === 'ftin'
          ? 'That height doesn’t look right — please check the feet and inches (e.g. 5 ft 8 in).'
          : 'That height doesn’t look right — please enter it in centimetres (e.g. 170).';
      }
      return null;
    }
    if (step.type === 'lr') {
      var l = num(state.values[step.left.prop]);
      var r = num(state.values[step.right.prop]);
      if (!l || !r) return 'Please enter a valid measurement for both legs.';
      return null;
    }
    if (step.type === 'single') {
      if (!num(state.values[step.input.key])) return 'Please enter a valid measurement.';
      return null;
    }
    if (step.type === 'pref') return null;   // defaults to Balanced — always answerable
    return null;
  }
  function num(v) { var n = parseFloat(v); return isNaN(n) || n <= 0 ? 0 : n; }

  function go(i) {
    if (i < 0 || i > steps().length - 1) return;
    current = i;
    renderStep(steps()[current]);
    updateNav();
    stage.scrollTop = 0;
    if (overlay.querySelector('.ff-overlay__panel')) overlay.querySelector('.ff-overlay__panel').scrollTop = 0;
  }

  function updateNav() {
    var last = current >= steps().length - 1;
    prevBtn.style.visibility = current === 0 ? 'hidden' : 'visible';
    var otsMode = isDual ? mode === 'ots' : CFG.productType === 'ots';
    nextBtn.innerHTML = last ? (otsMode ? 'Confirm size' : 'Confirm measurements') : 'Next Step <span aria-hidden="true">→</span>';
    var dots = '';
    for (var i = 0; i < steps().length; i++) dots += '<span class="ff-dot' + (i === current ? ' is-on' : '') + '"></span>';
    progressEl.innerHTML = dots;
  }

  /* ---------- Finish: write to cart form + gate ---------- */
  function finish() {
    setFlowComplete(isDual ? mode : null);
    saveState();
    var mtmFlow = isDual ? mode === 'mtm' : CFG.productType === 'mtm';
    if (mtmFlow) {
      writeProperties();
      if (state.shoeSize) selectFootVariant(state.shoeSize);
      document.dispatchEvent(new CustomEvent('rbc:mtm-complete', { detail: { productId: CFG.productId } }));
      // The rider's fresh measurements satisfy the standard flow too — derive
      // and prefill it NOW, not just on the next page load (toggling to
      // standard right after a first MTM completion showed no recommendation
      // until refresh — Simon, launch day). FORCED: the just-confirmed capture
      // supersedes any earlier standard-flow entry, and the previous size
      // choice is recomputed from it (latest measurements win — Simon 2026-07-04).
      deriveOtsFromMtm(state.values, true);
      state.mtmConfirmed = true;   // the walkthrough just confirmed these L/R values
      state.chosen = null;
      state.chosenManual = false;
      if (isDual && !state.otsComplete && flowSatisfied(CFG.otsSteps, state)) state.otsComplete = true;
      saveState();
      if (isDual && state.otsComplete) applyRecommendation();
    } else {
      // Reverse direction of latest-wins: a fresh standard entry that contradicts
      // the saved MTM capture voids it — the walkthrough re-opens for a re-take
      // (prefilled, with a notice) before any MTM order, rather than silently
      // attaching measurements the rider just corrected (Simon 2026-07-04).
      if (isDual && state.mtmComplete && mtmDisagrees(state.values)) { state.mtmComplete = false; saveState(); }
      applyRecommendation();
    }
    relabelTrigger();
    close();
  }
  function applyRecommendation() {
    var rec = otsRec();
    // Off-chart on a standard-sizes-only style (e.g. Tricolore): there is no MTM
    // variant to route to — select nothing and leave the customer in the results'
    // contact-us state rather than pretending a size fits.
    if (rec && rec.verdict === 'outside' && !state.chosenManual && isDual && !CFG.mtmCapable) return;
    // Honest-fit guard: if measurements are off our standard chart and the customer hasn't
    // deliberately overridden to an in-chart size, do NOT quietly select a standard size —
    // route to made-to-measure (the stock/returns protection). Deliberate manual picks stand.
    if (rec && rec.verdict === 'outside' && !state.chosenManual) {
      state.chosen = state.chosen || {};
      state.chosen.route = 'made-to-measure';
      saveState();
      document.dispatchEvent(new CustomEvent('rbc:choose-mtm', { detail: { productId: CFG.productId } }));
      selectPropertyOption('Fit', 'Made to measure');
      return;
    }
    var ch = state.chosen;
    // Full option LABEL first (same rule as buildRecommendation's `auto`):
    // selectFootVariant exact-matches radio values ("EU 38 (UK 5)"), so the bare
    // chart number ("38") selects nothing — on a profile-seeded load (chosen never
    // crosses products) height/calf applied but the buy-box foot silently stayed
    // on the page-load default, and the WRONG variant could go to cart (Simon's
    // S20 test, 2026-07-06).
    if (!ch && rec) ch = { foot: state.shoeSize || String(rec.shoe), heightName: rec.leg && rec.leg.name, calfName: rec.calf && rec.calf.name };
    if (!ch) return;
    if (ch.foot) selectFootVariant(String(ch.foot));
    if (ch.heightName) {
      // The size stack's height options are named by CODE ("Tall (MA)") while the chart
      // names differ ("Extra Short" vs "X Short") — match on the (code) first.
      var code = rec ? heightCodeForName(rec.legP, ch.heightName) : '';
      var byCode = code && selectPropertyOption(CFG.recommendTargets.legHeight, '(' + code + ')');
      if (!byCode) selectPropertyOption(CFG.recommendTargets.legHeight, ch.heightName);
    }
    if (ch.calfName) selectPropertyOption(CFG.recommendTargets.calf, ch.calfName);
  }

  function cartForm() {
    // Shopify's installments banner renders its own form[action*="/cart/add"] FIRST in
    // the DOM — writing properties into it silently loses them. Prefer the form that
    // actually owns the add-to-cart button.
    var forms = document.querySelectorAll('form[action*="/cart/add"], form[action$="/cart/add"]');
    var fallback = null;
    for (var i = 0; i < forms.length; i++) {
      if ((forms[i].getAttribute('id') || '').indexOf('installment') !== -1) continue;
      if (forms[i].querySelector('[data-to-cart]')) return forms[i];
      if (!fallback) fallback = forms[i];
    }
    return fallback;
  }

  function measurementProps() {
    // Only the walkthrough's L/R measurement props — state.values also holds OTS keys
    // (legHeight, prefCalf, …) that must never ride along as order properties.
    var arr = isDual ? CFG.mtmSteps : CFG.steps;
    var list = [];
    (arr || []).forEach(function (s) {
      if (s.type === 'lr') { list.push(s.left.prop); list.push(s.right.prop); }
    });
    if (stepsHaveLr(arr)) list.push(RIDER_HEIGHT_PROP);
    return list;
  }

  /* Measurements go into the form that is being submitted. With no form given (finish(), page-load restore) that is
     the page's own cart form; the submit gate passes the submitting form, which on a page with a second cart form for
     the same product is not necessarily the first one in the DOM. */
  function writeProperties(target) {
    var form = target || cartForm();
    if (!form) return;
    measurementProps().forEach(function (prop) {
      var val = state.values[prop];
      if (val === undefined || val === null || val === '') return;
      var name = 'properties[' + prop + ']';
      var input = form.querySelector('input[name="' + cssEscape(name) + '"][data-ff="1"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.setAttribute('data-ff', '1');
        form.appendChild(input);
      }
      input.value = val;
    });
  }

  function cssEscape(s) { return s.replace(/(["\\])/g, '\\$1'); }

  function selectFootVariant(value) {
    if (!value) return;
    value = String(value).trim();
    // ShowTime drives the SUBMITTED variant from the option radios that carry
    // data-variant-id: <product-variant-selector> reads them on `change` and
    // publishes variant:change to the variant provider. The visible a11y
    // <select> has only data-option-index and no variant id, so changing it
    // never moves the cart variant — we must check the radio, not the select.
    var scope = document.querySelector('product-variant-selector');
    if (scope) {
      var radios = scope.querySelectorAll('input[type="radio"][data-option-index]');
      for (var i = 0; i < radios.length; i++) {
        var r = radios[i];
        if (r === document.getElementById('ff-shoe')) continue;
        if (String(r.value).trim() === value) {
          r.checked = true;
          syncingFoot = true;
          r.dispatchEvent(new Event('change', { bubbles: true }));
          syncingFoot = false;
          // Keep the sibling a11y <select> label in sync for keyboard users.
          var dd = r.closest('product-options-dropdown');
          var nativeSel = dd && dd.querySelector('select');
          if (nativeSel) nativeSel.value = r.value;
          // The dropdown's visible toggle label ([data-current]) only updates
          // on the component's own item clicks — sync it or the customer sees
          // the old size while the variant has moved (Parlanti, launch day).
          var cur = dd && dd.querySelector('[data-current]');
          if (cur) cur.textContent = r.value;
          return;
        }
      }
      return;
    }
    // Fallback (no <product-variant-selector> present): a genuine native
    // <select>/radio variant picker.
    var sels = document.querySelectorAll('select, input[type="radio"]');
    for (var k = 0; k < sels.length; k++) {
      var c = sels[k];
      if (c === document.getElementById('ff-shoe')) continue;
      var opts = c.tagName === 'SELECT' ? c.options : [c];
      for (var j = 0; j < opts.length; j++) {
        if (String(opts[j].value).trim() === value ||
            String(opts[j].textContent || '').trim() === value) {
          if (c.tagName === 'SELECT') { c.value = opts[j].value; }
          else { c.checked = true; }
          syncingFoot = true;
          c.dispatchEvent(new Event('change', { bubbles: true }));
          syncingFoot = false;
          return;
        }
      }
    }
  }

  /* ---------- Trigger button + add-to-cart gating ---------- */
  function triggers() { return document.querySelectorAll('.js-open-fitfinder'); }

  function relabelTrigger() {
    triggers().forEach(function (b) {
      var label = b.querySelector('.ff-trigger-label') || b;
      var m = isDual ? (b.getAttribute('data-ff-mode') || 'ots') : CFG.productType;
      if (m === 'mtm') {
        // Values present but completion voided (a fresh standard entry
        // contradicted the L/R capture, or measurements carried from another
        // boot): it's a re-check, not a from-scratch measure.
        var retake = !flowComplete('mtm') && flowSatisfied(isDual ? CFG.mtmSteps : CFG.steps, state);
        label.textContent = flowComplete('mtm') ? 'Update measurements'
          : retake ? 'Re-check measurements'
          : (b.getAttribute('data-ff-label') || CFG.triggerLabel || 'Add Your Measurements');
        b.classList.toggle('is-complete', flowComplete('mtm'));
      } else if (m === 'ots') {
        // Measurements carried from another boot but the flow isn't complete
        // here (usually a foot size in a different brand's vocabulary):
        // "Find My Size" would imply starting over — it's a 10-second confirm.
        var carried = !flowComplete('ots') && num(state.values.legHeight) > 0 && num(state.values.calfSize) > 0;
        label.textContent = flowComplete('ots') ? 'Update my size'
          : carried ? 'Confirm my size'
          : (b.getAttribute('data-ff-label') || (isDual ? 'Find My Size' : CFG.triggerLabel) || 'Find My Size');
        b.classList.toggle('is-complete', flowComplete('ots'));
      }
    });
  }

  /* product-to-cart flips its button to !loading on click and only clears it on
     cart:update/cart:error — clear it ourselves whenever we block a submit. */
  function unstickCartButtons(form) {
    var btns = form.querySelectorAll('[data-to-cart]');
    for (var i = 0; i < btns.length; i++) btns[i].classList.remove('!loading');
  }
  /* Is the variant this form is about to SUBMIT a Made to Measure one? The id the form carries is the truth; the
     option radios are only a view of it and have disagreed with it (a page opened on a Made to Measure link showed
     "Standard" ticked while still submitting the +£160 variant — the gate read the radio and let it through with
     no measurements). The radios remain the fallback for a config without the id list. */
  function mtmVariantSelected(form) {
    var ids = CFG.mtmVariantIds;
    if (form && ids && ids.length) {
      var id = null;
      try { id = new FormData(form).get('id'); } catch (err) { id = null; }
      if (id) return ids.indexOf(String(id)) !== -1;
    }
    var scope = document.querySelector('product-variant-selector');
    if (!scope) return false;
    var checked = scope.querySelectorAll('input[type="radio"][data-option-index]:checked');
    for (var i = 0; i < checked.length; i++) {
      if (String(checked[i].value).trim() === 'Made to Measure') return true;
    }
    return false;
  }
  function removeFFProps(form) {
    var stray = form.querySelectorAll('input[data-ff="1"]');
    for (var i = 0; i < stray.length; i++) stray[i].parentNode.removeChild(stray[i]);
  }

  /* The gates below listen on document, so they see EVERY cart form on the page. A form for a different product (a
     quick view, a featured-product block) must not be judged by this product's variant ids, and this walkthrough
     cannot capture its measurements. If that form's selected variant needs them (the server says so on the form),
     hold the submit and take the customer to that product's own page, where its Fit Finder runs. Returns true when
     the form was not this product's. */
  function foreignForm(e, form) {
    var mark = form.querySelector('[data-rbc-form-product]');
    if (!mark || String(mark.getAttribute('data-rbc-form-product')) === String(CFG.productId)) return false;
    if (mark.getAttribute('data-rbc-requires-measurements') === 'true' && !e.defaultPrevented) {
      if (window.rbcHoldForMeasurements) window.rbcHoldForMeasurements(e, form);   // one implementation (rbc-measure-guard.js)
      else {
        e.preventDefault();
        e.stopImmediatePropagation();
        unstickCartButtons(form);
        var url = mark.getAttribute('data-rbc-product-url');
        var id = null;
        try { id = new FormData(form).get('id'); } catch (err) { id = null; }
        if (url) window.location.href = url + (id ? (url.indexOf('?') === -1 ? '?' : '&') + 'variant=' + encodeURIComponent(id) : '');
      }
    }
    return true;
  }

  function gateAddToCart() {
    // Forms for OTHER products are held whatever this product's own flow is (a Tricolore or off-the-shelf page has no
    // measurement gate of its own, but can still host another product's form).
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (form && form.matches && form.matches('form[action*="/cart/add"]')) foreignForm(e, form);
    }, true);
    if (isDual) {
      if (!CFG.mtmCapable) return;
      // Tell the form's own guard (rbc-measure-guard.js) that this product's walkthrough gate is running: from here
      // on this listener decides for its forms. Set only together with the listener itself.
      window.rbcFitFinderGate = String(CFG.productId);
      document.addEventListener('submit', function (e) {
        var form = e.target;
        if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;
        if (foreignForm(e, form)) return;
        if (!mtmVariantSelected(form)) { removeFFProps(form); return; }   // standard order → no stray measurement props
        if (flowComplete('mtm')) { writeProperties(form); return; }   // measurements ride along at submit time, in THIS form
        e.preventDefault();
        e.stopImmediatePropagation();
        unstickCartButtons(form);
        flashTrigger();
        mode = 'mtm';
        open();
      }, true);
      return;
    }
    if (CFG.productType !== 'mtm') return;
    window.rbcFitFinderGate = String(CFG.productId);
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;
      if (foreignForm(e, form)) return;
      if (state.complete) { writeProperties(form); return; }   // into THIS form: finish() only wrote the page's first cart form
      e.preventDefault();
      e.stopImmediatePropagation();
      unstickCartButtons(form);
      flashTrigger();
      open();
    }, true);
  }

  function flashTrigger() {
    triggers().forEach(function (b) {
      b.classList.add('ff-shake');
      setTimeout(function () { b.classList.remove('ff-shake'); }, 1500);
    });
    var msg = document.querySelector('.fit-finder-message, .js-fitfinder-message');
    if (msg) { msg.removeAttribute('hidden'); msg.classList.add('is-on'); }
  }

  /* ---------- Open / close ---------- */
  /* The buy-box's checked Foot Size radio is the freshest signal — identify it
     among the checked option radios by membership in CFG.footSizes (the Fit
     option's values never collide with foot labels). But the page-load DEFAULT
     (first variant, EU 34) is not customer intent: it only outranks a saved
     wizard size once the customer has actually touched the buy-box (footTouched).
     Our own write-backs via selectFootVariant must not count as touches. */
  var footTouched = false;
  var syncingFoot = false;
  document.addEventListener('change', function (e) {
    if (syncingFoot) return;
    var t = e.target;
    if (t && t.matches && t.matches('input[type="radio"][data-option-index]') &&
        (CFG.footSizes || []).indexOf(String(t.value).trim()) !== -1) footTouched = true;
  });
  function buyBoxFootSize() {
    var scope = document.querySelector('product-variant-selector');
    if (!scope) return '';
    var checked = scope.querySelectorAll('input[type="radio"][data-option-index]:checked');
    for (var i = 0; i < checked.length; i++) {
      var v = String(checked[i].value).trim();
      if ((CFG.footSizes || []).indexOf(v) !== -1) return v;
    }
    return '';
  }
  function open() {
    // Adopt the buy-box foot ONLY when the customer actually touched it — the
    // page-load default (first variant: '2.5' on Parlanti, 'EU 34' on De Niro)
    // is not their answer, and pre-answering the shoe step with it reads as
    // "auto-selected the wrong size" (Simon, launch day).
    var foot = buyBoxFootSize();
    if (foot && foot !== state.shoeSize && footTouched) {
      state.shoeSize = foot;
      state.chosenManual = false; state.chosen = null;  // foot changed → recompute recommendation
      saveState();
    }
    if (!overlay) buildOverlay();
    // Always re-enter at the first step, answers pre-filled (staff round 1,
    // Decision 5): a completed flow that jumped straight to results read as
    // "it skipped the guide". Next steps through quickly since every input
    // is already answered from state.
    go(0);
    overlay.removeAttribute('hidden');
    document.documentElement.classList.add('ff-open');
    document.addEventListener('keydown', onKey);
  }
  function close() {
    if (!overlay) return;
    overlay.setAttribute('hidden', '');
    document.documentElement.classList.remove('ff-open');
    document.removeEventListener('keydown', onKey);
    relabelTrigger();   // mid-flow edits may have voided a completion — labels re-check state
  }
  function onKey(e) { if (e.key === 'Escape') close(); }

  function showError(msg) { errEl.textContent = msg; errEl.removeAttribute('hidden'); }
  function hideError() { errEl.setAttribute('hidden', ''); errEl.textContent = ''; }

  /* ---------- Boot ---------- */
  function init() {
    triggers().forEach(function (b) {
      b.addEventListener('click', function (e) {
        e.preventDefault();
        if (isDual) mode = b.getAttribute('data-ff-mode') || 'ots';
        open();
      });
    });
    relabelTrigger();
    gateAddToCart();
    // Re-apply captured state on load so a returning/complete state submits/recommends
    // correctly. Dual MTM properties are written at submit time instead (the customer
    // may reload onto a standard order — stray measurement props must never attach).
    if (isDual) {
      if (state.otsComplete) { mode = 'ots'; whenVariantMachineryReady(applyRecommendation); }
      // finish() (which writes the foot back) never runs on a seeded/reloaded
      // page — without this the buy-box sits on the page-load default (EU 34)
      // while ATC is unlocked, and the WRONG variant goes to the cart.
      if (state.mtmComplete) syncFootWhenReady();
    } else if (state.complete) {
      if (CFG.productType === 'mtm') { writeProperties(); syncFootWhenReady(); }
      // Deferred for the same reason as syncFootWhenReady: at DOMContentLoaded
      // the async-registered selector/provider aren't listening yet, so the
      // foot write-back's change event is LOST and add-to-cart submits the
      // page-load default variant (caught live on Parlanti, launch day).
      else whenVariantMachineryReady(applyRecommendation);
    }
  }

  // Run a variant-writing action once the machinery is up. The submitted
  // variant lives in <product-variant-provider>, which subscribes to the
  // selector's change AFTER both elements define — so wait on BOTH, and still
  // retry on timers: re-running is idempotent, but a change event nobody heard
  // is a wrong-variant order.
  function whenVariantMachineryReady(fn) {
    if (window.customElements && customElements.whenDefined) {
      Promise.all([
        customElements.whenDefined('product-variant-selector'),
        customElements.whenDefined('product-variant-provider'),
      ]).then(function () { setTimeout(fn, 0); });
    }
    setTimeout(fn, 800);
    setTimeout(fn, 2000);
  }
  // Write the saved foot size back to the buy-box once the variant machinery is
  // up. The touch-guard rule holds: only the page-load default is overridden —
  // a foot the customer has actually touched wins.
  function syncFootWhenReady() {
    if (!state.shoeSize) return;
    whenVariantMachineryReady(function () { if (!footTouched) selectFootVariant(state.shoeSize); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
